/**
 * The customer's frame for a tab beyond Overview — Orders, Stock, Invoices,
 * Settings (docs/features/journey-visual-language/01_proposal.md §5.8): the
 * customer's name and account code, the tabs with this one current, then the
 * tab's own content.
 *
 * Self-loading: it reads the customer for its heading. The tab's content
 * loads and says what it needs on its own, so a missing heading never blocks it.
 */
import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router";
import { ChevronLeft } from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { isFeatureEnabled } from "@shared-core/config/features";
import { paths } from "@shared-core/routing/routes";
import { CreditChip } from "../credit-control/CreditChip";
import { CustomerTabs, type CustomerTabKey } from "./CustomerTabs";

export function CustomerFrame({
  customerId,
  current,
  children,
}: {
  customerId: string;
  current: CustomerTabKey;
  children: ReactNode;
}) {
  const adapter = useDataAdapter();
  const [identity, setIdentity] = useState<{
    name: string;
    code: string | null;
    cisCode: string | null;
  } | null>(null);

  useEffect(() => {
    let cancelled = false;
    adapter
      .getCustomerById(customerId)
      .then((detail) => {
        if (cancelled || !detail?.customer) return;
        setIdentity({
          name: detail.customer.name,
          code: detail.customer.customer_code ?? null,
          cisCode: detail.customer.cis_customer_code ?? null,
        });
      })
      .catch(() => {
        if (!cancelled) setIdentity(null);
      });
    return () => {
      cancelled = true;
    };
  }, [adapter, customerId]);

  return (
    <div className="space-y-4">
      <Link
        to={paths.customers()}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ChevronLeft className="size-4" />
        Customers
      </Link>
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2.5">
          <h1 className="text-2xl font-semibold tracking-tight">
            {identity?.name ?? customerId}
          </h1>
          {identity?.code && (
            <span className="rounded border border-border px-1.5 py-0.5 text-xs font-medium text-muted-foreground">
              {identity.code}
            </span>
          )}
          {isFeatureEnabled("v1_completion") && identity?.cisCode && (
            <span className="rounded border border-border px-1.5 py-0.5 text-xs font-medium text-muted-foreground">
              {identity.cisCode}
            </span>
          )}
          <CreditChip customerId={customerId} />
        </div>
        <CustomerTabs
          customerId={customerId}
          customerCode={identity?.code}
          customerName={identity?.name}
          cisCustomerCode={identity?.cisCode}
          current={current}
        />
      </div>
      {children}
    </div>
  );
}
